import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { booleanConverter } from './common/converters';
import { TeComponent } from './common/te-component';
import { theme } from './common/theme';
import { TRANSITION_DURATION_FAST } from './common/transition';
import './te-text';

/** Toggle Component */
@customElement('te-toggle')
export class TeToggle extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-toggle-wrapper {
        position: relative;
        display: flex;
        width: fit-content;
        align-items: center;
      }

      .te-toggle-input {
        opacity: 0;
        position: absolute;
        margin: 0;
        padding: 0;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        cursor: pointer;
      }

      .te-toggle-label {
        padding: 0 16px;
      }

      .te-toggle-track,
      .te-toggle-slider {
        outline: none;
        border: none;
        position: relative;
        height: 24px;
        width: 48px;
        border-radius: 12px;
        background-color: ${theme.colors.shade3};
        cursor: pointer;
      }

      .te-toggle-track {
        transition-property: background-color;
        transition-duration: ${TRANSITION_DURATION_FAST}ms;
      }

      .toggled .te-toggle-track {
        background-color: ${theme.colors.primary.v0};
      }

      .te-toggle-slider {
        top: 2px;
        left: 2px;
        position: absolute;
        height: 20px;
        width: 20px;
        background-color: ${theme.colors.shade1};
        transition-property: left;
        transition-duration: ${TRANSITION_DURATION_FAST}ms;
      }

      .toggled .te-toggle-track .te-toggle-slider {
        left: 26px;
      }

      .disabled .te-toggle-track {
        cursor: default;
        background-color: ${theme.colors.shade2};
      }

      .disabled.te-toggle-slider {
        cursor: default;
        background-color: ${theme.colors.shade0};
      }

      .toggled.disabled .te-toggle-track {
        background-color: ${theme.colors.primary.v0};
        opacity: 0.1;
      }

      .disabled .te-toggle-input {
        cursor: default;
      }

      .te-toggle-wrapper.disabled {
        background-color: ${theme.colors.shade0};
      }
    `,
  ];

  /** Toggle is toggled */
  @property({ type: Boolean, converter: booleanConverter })
  toggled = false;
  /** Toggle is disabled */
  @property({ type: Boolean, converter: booleanConverter })
  disabled = false;

  handleHiddenInputChange(e: Event) {
    if (this.disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    this.toggled = (e.currentTarget as HTMLInputElement).checked;
    this.handleChange(e);
    this.handleToggle(e);
    this.handleClick(e);
  }

  render() {
    const toggleClass = ['te-toggle-wrapper'];
    this.disabled && toggleClass.push('disabled');
    this.toggled && toggleClass.push('toggled');

    return html`
      <div class="${toggleClass.join(' ')}">
        <div class="te-toggle-track">
          <div class="te-toggle-slider"></div>
        </div>
        <input
          type="checkbox"
          .value=${this.toggled.toString()}
          .checked=${this.toggled}
          .disabled=${this.disabled}
          class="te-toggle-input"
          @change=${this.handleHiddenInputChange}
        />
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-toggle': TeToggle;
  }
}
